import React, { useState, useEffect } from "react";
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import "./styles/main.scss";
import LandingPage from "./components/landing-pages/LandingPage";
import Signup from "./components/Signup";
import Login from "./components/Login";
import VerifyEmail from "./components/VerifyEmail";
import ForgotPassword from "./components/ForgotPassword";
import Dashboard from "./components/Dashboard";
import Metrics from "./components/Metrics";
import AccountInfo from "./components/AccountInfo";
import JobSearch from "./components/JobSearch";
import ResumeBuilder from "./components/ResumeBuilder";
// import NewResumeBuilder from "./components/resume-builder/ResumeBuilder";
import Preferences from "./components/Preferences";
import SEO from "./SEO";
import EmailVerified from "./components/EmailVerified";
import Onboarding from "./components/Onboarding";
import "@radix-ui/themes/styles.css";
import JobPage from "./components/landing-pages/JobPage";
import PortfolioPage from "./components/Portfolio";
import PricingPage_second from "./components/landing-pages/Pricing";
import About from "./components/landing-pages/About";
import "./index.css";
import ScrollToTop from "./components/ScrollToTop";
import FaviconManager from "./components/FaviconManager";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { googleClientId } from "./constant";
import GoogleRedirect from "./components/GoogleRedirect";
import Chat from "./components/chat/Chat";
import AiMockInterview from "./components/AiMockInterview";
import MockInterview from "./components/MockInterview";
import TermsOfService from "./components/TermsofService";
import PrivacyPolicy from "./components/PrivacyPolicy";
import BringYourJob from "./components/bringYourJob/BringYourJob";
import ApplicationTracker from "./components/applicationTracker/ApplicationTracker";
import ResumeTailoringPage from "./components/landing-pages/ResumeTailoringPage";
import CoverLetterPage from "./components/landing-pages/CoverLetterPage";
import MockInterviewsPage from "./components/landing-pages/MockInterviewsPage";
import ApplicationTrackerPage from "./components/landing-pages/ApplicationTrackerPage";
import ChatPage from "./components/landing-pages/ChatPage";
import CareerCoachingPage from "./components/landing-pages/CareerCoachingPage";
import { ApplicationModalProvider } from "./contexts/ApplicationModalContext";
import ApplicationConfirmModal from "./components/ApplicationConfirmModal";
import SubscriptionPlans from "./components/Subscription/SubscriptionPlans";
import DevTestJobSearch from "./components/DevTestJobSearch";
import OutreachPage from "./components/landing-pages/OutreachPage";
import NotFound from "./components/landing-pages/NotFound";
import axiosClient from "./axios-client";
import SuccessPage from "./components/SuccessPage";
import CancelPage from "./components/CancelPage";
import ContactUs from "./components/ContactUs";
import Changelog from "./components/landing-pages/Changelog";
import Tutorials from "./components/tutorials/Tutorials";
import { JobModalProvider } from "./contexts/JobModalContext";
import GlobalJobModal from "./components/GlobalJobModal";
import { companyInfoService } from "./services/companyInfo.service";
import { UserProvider } from './contexts/UserContext'; // Adjust path

const Loading: React.FC = () => (
  <div
    style={{
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      height: "100vh",
    }}
  >
    <div className="spinner"></div>
  </div>
);

// Helper function to save intended URL before redirecting to login
const SaveUrlAndRedirect: React.FC = () => {
  const location = useLocation();
  
  useEffect(() => {
    // Save the current URL to localStorage so we can redirect back after login
    const pathToSave = location.pathname + location.search;
    console.log("Saving redirect path to localStorage:", pathToSave);
    localStorage.setItem('redirectAfterLogin', pathToSave);
  }, [location]);
  
  // Use Navigate component directly for more reliable redirects
  return <Navigate to="/auth/login" />;
};

// Layout component with required context providers for authenticated routes
const AuthenticatedLayout: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  return (
    <JobModalProvider>
      {children}
      <GlobalJobModal />
    </JobModalProvider>
  );
};

// Component to handle route protection
const RequireAuth: React.FC<{ user: any, children: React.ReactNode }> = ({ user, children }) => {
  if (!user) {
    return <SaveUrlAndRedirect />;
  }
  
  return <AuthenticatedLayout>{children}</AuthenticatedLayout>;
};

function App() {
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initializeApp = async () => {
      // Check authentication
      const checkAuth = () => {
        const token = localStorage.getItem("accessToken");
        const storedUser = localStorage.getItem("user");
  
        if (token && storedUser) {
          try {
            const parsedUser = JSON.parse(storedUser);
            setUser(parsedUser);
            // Set the token in axios default headers
            axiosClient.defaults.headers.common[
              "Authorization"
            ] = `Bearer ${token}`;
          } catch (error) {
            console.error("Error parsing user data:", error);
            localStorage.removeItem("accessToken");
            localStorage.removeItem("user");
            setUser(null);
          }
        } else {
          setUser(null);
        }
      };
      
      // Preload company industry data in parallel with auth check
      try {
        // Preload company industry data for fast access
        await companyInfoService.preloadCompanyIndustries();
        console.log("Company industry data preloaded successfully");
      } catch (error) {
        console.error("Error preloading company data:", error);
        // Continue even if preloading fails - app should still work
      }
      
      // Complete auth check
      checkAuth();
      setLoading(false);
    };

    initializeApp();
  }, []);

  if (loading) {
    return <Loading />;
  }

  // Guest-friendly redirect: if a non-authenticated visitor lands on a
  // dashboard-style share link, rewrite it to the public job-search URL *before*
  // the router renders anything else.  This avoids a brief hop to /auth/login.
  if (!user && typeof window !== 'undefined') {
    const match = window.location.pathname.match(/^\/dashboard\/job\/([^/]+)/);
    if (match) {
      const jobId = match[1];
      window.history.replaceState({}, '', `/job-search/job/${jobId}`);
    }
  }

  return (
    <ApplicationModalProvider>
      <GoogleOAuthProvider clientId={googleClientId}>
        <UserProvider>
        <Router>
          <ScrollToTop />
          <FaviconManager />
          <SEO />

          {/* Render routes with modal overlay handling */}
          <AppRoutes user={user} setUser={setUser} />
        </Router>
        </UserProvider>
      </GoogleOAuthProvider>
    </ApplicationModalProvider>
  );
}

const Logout: React.FC<{ setUser: (user: any) => void }> = ({ setUser }) => {
  const navigate = useNavigate();

  useEffect(() => {
    const logoutUser = () => {
      localStorage.removeItem("accessToken");
      localStorage.removeItem("user");
      setUser(null);
      navigate("/");
    };

    logoutUser();
  }, [navigate, setUser]);

  return null;
};

// Separated component so we can safely use hooks inside
const AppRoutes: React.FC<{ user: any; setUser: (u: any) => void }> = ({ user, setUser }) => {
  const location = useLocation();
  const bgLocation = (location.state as any)?.bgLocation;

  return (
    <>
      <Routes location={bgLocation || location}>
        {/* Public Routes - No auth required */}
        <Route path="/" element={user ? <Navigate to="/dashboard" /> : <LandingPage />} />
        <Route path="/auth/signup" element={<Signup />} />
        <Route 
          path="/auth/login" 
          element={
            user ? (
              // If user is already logged in, check if there's a redirect path
              (() => {
                const savedPath = localStorage.getItem('redirectAfterLogin');
                if (savedPath) {
                  console.log("User already logged in, redirecting to saved path:", savedPath);
                  localStorage.removeItem('redirectAfterLogin');
                  return <Navigate to={savedPath} replace />;
                } else {
                  console.log("User already logged in, no saved path, redirecting to dashboard");
                  return <Navigate to="/dashboard" replace />;
                }
              })()
            ) : (
              <Login />
            )
          } 
        />
        <Route path="/auth/verify-email" element={<VerifyEmail />} />
        <Route path="/auth/forgot-password" element={<ForgotPassword />} />
        <Route path="/auth/email-verified" element={<EmailVerified />} />
        <Route path="/auth/google/callback" element={<GoogleRedirect />} />
        <Route path="/logout" element={<Logout setUser={setUser} />} />
        <Route path="/portfolio/:username" element={<PortfolioPage />} />
        <Route path="/pricing" element={<PricingPage_second />} />
        <Route path="/about" element={<About />} />
        <Route path="/terms" element={<TermsOfService />} />
        <Route path="/privacy" element={<PrivacyPolicy />} />
        <Route path="/changelog" element={<Changelog />} />
        <Route path="/contact-us" element={<ContactUs />} />
        <Route path="/jobs" element={<JobPage />} />
        <Route path="/jobs/job/:jobId" element={<JobPage />} />
        <Route path="/features" element={<Navigate to="/" replace />} />
        <Route path="/features/resume-tailoring" element={<ResumeTailoringPage />} />
        <Route path="/features/cover-letter" element={<CoverLetterPage />} />
        <Route path="/features/mock-interviews" element={<MockInterviewsPage />} />
        <Route path="/features/application-tracker" element={<ApplicationTrackerPage />} />
        <Route path="/features/outreach" element={<OutreachPage />} />
        <Route path="/features/career-coaching" element={<CareerCoachingPage />} />
        
        {/* Protected Routes - Auth required */}
        <Route path="/dashboard" element={
          <RequireAuth user={user}>
            <Dashboard />
          </RequireAuth>
        } />
        
        <Route path="/dashboard/job/:jobId" element={
          <RequireAuth user={user}>
            <Dashboard />
          </RequireAuth>
        } />
        
        <Route path="/metrics" element={
          <RequireAuth user={user}>
            <Metrics />
          </RequireAuth>
        } />
        
        <Route path="/profile" element={
          <RequireAuth user={user}>
            <AccountInfo />
          </RequireAuth>
        } />
        
        <Route path="/job-search" element={
          <RequireAuth user={user}>
            <JobSearch />
          </RequireAuth>
        } />
        
        <Route path="/job-search/job/:jobId" element={
          user ? (
            <AuthenticatedLayout>
              <JobSearch />
            </AuthenticatedLayout>
          ) : (
            <JobModalProvider>
              <JobSearch />
              <GlobalJobModal />
            </JobModalProvider>
          )
        } />
        
        <Route path="/job-search/preferences" element={
          <RequireAuth user={user}>
            <Preferences onSave={(handleSavePreferences) => {}} />
          </RequireAuth>
        } />
        
        <Route path="/job-search/jobs" element={
          <RequireAuth user={user}>
            <JobSearch />
          </RequireAuth>
        } />
        
        <Route path="/job-search/jobs/job/:jobId" element={
          <RequireAuth user={user}>
            <JobSearch />
          </RequireAuth>
        } />
        
        <Route path="/job-search/dev-test" element={
          <RequireAuth user={user}>
            <DevTestJobSearch />
          </RequireAuth>
        } />
        
        <Route path="/legacy-resume-system-v1-archive" element={
          <RequireAuth user={user}>
            <ResumeBuilder />
          </RequireAuth>
        } />
        
        <Route path="/ai-mock-interview" element={
          <RequireAuth user={user}>
            <AiMockInterview />
          </RequireAuth>
        } />
        
        <Route path="/mock-interview" element={
          <RequireAuth user={user}>
            <MockInterview />
          </RequireAuth>
        } />
        
        <Route path="/onboarding" element={
          user ? (
            user.userOnboarding ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <RequireAuth user={user}>
                <Onboarding />
              </RequireAuth>
            )
          ) : (
            <SaveUrlAndRedirect />
          )
        } />
        
        <Route path="/stripe" element={
          <RequireAuth user={user}>
            <SubscriptionPlans />
          </RequireAuth>
        } />
        
        <Route path="/subscription" element={
          <RequireAuth user={user}>
            <SubscriptionPlans />
          </RequireAuth>
        } />
        
        <Route path="/tutorials" element={
          <RequireAuth user={user}>
            <Tutorials />
          </RequireAuth>
        } />
        
        <Route path="/bring-your-job" element={
          <RequireAuth user={user}>
            <BringYourJob />
          </RequireAuth>
        } />
        
        <Route path="/bring-your-job/job/:jobId" element={
          <RequireAuth user={user}>
            <BringYourJob />
          </RequireAuth>
        } />
        
        <Route path="/application-tracker" element={
          <RequireAuth user={user}>
            <ApplicationTracker />
          </RequireAuth>
        } />
        
        <Route path="/application-tracker/job/:jobId" element={
          <RequireAuth user={user}>
            <ApplicationTracker />
          </RequireAuth>
        } />
        
        <Route path="/payment/success" element={
          <RequireAuth user={user}>
            <SuccessPage />
          </RequireAuth>
        } />
        
        <Route path="/payment/cancel" element={
          <RequireAuth user={user}>
            <CancelPage />
          </RequireAuth>
        } />
        
        {/* Redirect legacy account-info path */}
        <Route path="/account-info" element={<Navigate to="/profile" replace />} />
        
        {/* 404 Route */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      
      <JobModalProvider>
        <ApplicationConfirmModal />
      </JobModalProvider>

      {/* Modal overlay handled by JobModalContext */}
    </>
  );
};

export default App;
